/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import {
  Banner,
  Button,
  ButtonGroup,
  Card,
  FormLayout,
  Icon,
  Link,
  Page,
  Text,
  TextField,
  Modal,
  BlockStack,
  Spinner,
  Tooltip,
} from "@shopify/polaris";
import {
  ViewIcon,
  HideIcon,
  StoreIcon,
  InfoIcon,
} from "@shopify/polaris-icons";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { login } from "../store/store/slices/AuthSlice";
import { Controller, useForm } from "react-hook-form";
import { IS_AUTHORIZED_TYPE } from "../store/types/types";
import { useLoginUserAccountMutation } from "../store/store/slices/LoginRTK";
import { useSwitchStoreLoginMutation } from "../store/store/slices/UsersRTK";
import { showToast } from "../store/store/slices/AlertSlice";
import ForgetPassword from "../components/molecules/ForgetPassword";
import TurnstileWidget from "../components/atoms/TurnstileWidget";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [captchaKey, setCaptchaKey] = useState(0);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [searchParams] = useSearchParams();
  const token = searchParams?.get("token");
  const refreshTokenFromUrl = searchParams?.get("refresh_token");
  const [loginMode, setLoginMode] = useState(false);
  const { isAuthorized } = useSelector(
    (state: IS_AUTHORIZED_TYPE) => state?.auth,
  );
  const [err, setErr] = useState<string>("");
  const [loginUser, { isLoading, isSuccess, error, isError, data }] =
    useLoginUserAccountMutation<any>();
  const [showStoreSelection, setShowStoreSelection] = useState(false);
  const [storesData, setStoresData] = useState<any>(null);
  const [tempAuthToken, setTempAuthToken] = useState<string | null>(null);
  const [switchStoreLogin, { isLoading: isSwitchingStore }] =
    useSwitchStoreLoginMutation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (token) {
      localStorage.setItem("auth_token", token);
      if (refreshTokenFromUrl) {
        localStorage.setItem("refresh_token", refreshTokenFromUrl);
      }
      dispatch(login({ token } as any) as any);
    }
  }, []);
  useEffect(() => {
    dispatch(
      login({ token: localStorage.getItem("auth_token") } as any) as any,
    );
  }, []);

  useEffect(() => {
    if (isError) {
      setErr(() => error?.data?.errors[0]?.msg || "Something went wrong");
      // clear token
      setTurnstileToken(null);

      // force new captcha
      setCaptchaKey((prev) => prev + 1);
    }
    if (isSuccess && data) {
      // Check if stores selection is required
      if (data.stores_required === true) {
        // Store temporary token for switch-store API call
        if (data.auth_token) {
          setTempAuthToken(data.auth_token);
        }
        // Show store selection modal
        setStoresData({
          user: data.user,
          stores: data.stores || [],
        });
        setShowStoreSelection(true);
      } else {
        // Single store - use tokens directly
        if (data.auth_token) {
          localStorage.setItem("auth_token", data.auth_token);
          if (data.refresh_token) {
            localStorage.setItem("refresh_token", data.refresh_token);
            navigate("/dashboard");
          } else {
            dispatch(login({ token: data.auth_token } as any) as any);
          }
        }
        if (data.two_factor_await) {
          localStorage.setItem("two_factor_token", data.two_factor_token);
          navigate(`/two-factor?two_factor_token=${data.two_factor_token}`);
        }
      }
    }
  }, [isLoading, isSuccess, error, isError, data, dispatch, navigate]);

  useEffect(() => {
    if (isAuthorized) {
      navigate("/dashboard");
    }
  }, [isAuthorized, navigate]);

  const userLoginHandler = async (data: FormData) => {
    await loginUser({ ...data, token: turnstileToken });
  };

  const onShowPasswordHandler = () => {
    setShowPassword((prev) => !prev);
  };

  const handleStoreSelect = async (storeId: string) => {
    if (!tempAuthToken) {
      dispatch(
        showToast({
          message: "Authentication token is missing",
          error: true,
          visible: true,
        }),
      );
      return;
    }

    try {
      localStorage.setItem("auth_token", tempAuthToken);

      const response = await switchStoreLogin(storeId).unwrap();
      if (response.two_factor_await) {
        localStorage.setItem("auth_token", response.auth_token);
        localStorage.setItem("two_factor_token", response.two_factor_token);
        navigate(`/two-factor?two_factor_token=${response.two_factor_token}`);
        return;
      }
      // Save new tokens to localStorage (replace temporary token)
      if (response.token || response.data?.token) {
        const newToken = response.token || response.data?.token;
        localStorage.setItem("auth_token", newToken);
      }
      if (
        response.refreshToken ||
        response.refresh_token ||
        response.data?.refreshToken ||
        response.data?.refresh_token
      ) {
        const newRefreshToken =
          response.refreshToken ||
          response.refresh_token ||
          response.data?.refreshToken ||
          response.data?.refresh_token;
        localStorage.setItem("refresh_token", newRefreshToken);
      }

      // Update user data in localStorage
      if (response.user || response.data?.user) {
        const userData = response.user || response.data?.user;
        localStorage.setItem("user", JSON.stringify(userData));
      }

      // Clear temporary token state
      setTempAuthToken(null);

      // Close store selection modal
      setShowStoreSelection(false);

      // Complete login by dispatching login action
      const token = response.token || response.data?.token;
      if (token) {
        dispatch(login({ token } as any) as any);
      }
    } catch (error: any) {
      // Remove temporary token on error
      localStorage.removeItem("auth_token");

      // Handle error
      dispatch(
        showToast({
          message:
            error?.data?.message || error?.message || "Failed to select store",
          error: true,
          visible: true,
        }),
      );
    }
  };

  const handleThemeChange = () => {
    const newValue = theme === "light" ? "dark" : "light";
    localStorage.setItem("theme", newValue);
    window.dispatchEvent(
      new CustomEvent("themeChanged", { detail: { value: newValue } }),
    );
    // setTheme(newValue === "dark" ? "dark-experimental" : "light");
  };

  useEffect(() => {
    // listen for theme changes
    const handleThemeChange = (e: CustomEvent) => {
      setTheme(e.detail.value);
    };

    window.addEventListener("themeChanged", handleThemeChange as EventListener);
    return () =>
      window.removeEventListener(
        "themeChanged",
        handleThemeChange as EventListener,
      );
  }, []);

  const themeStyles = `
  .Polaris-TextField__Backdrop,
  .Polaris-Select__Input,
  .Polaris-Select__Backdrop,
  .Polaris-IndexTable__LoadingPanel,
  .Polaris-IndexTable__LoadingPanelRow {
    background-color: #1a1a1a !important;
    border-bottom: 1px solid #333 !important;
  }
  `;
  return (
    <>
      <style>{theme === "dark" && themeStyles}</style>
      <Modal
        open={showStoreSelection}
        onClose={() => !isSwitchingStore && setShowStoreSelection(false)}
        title='Select the store'
        primaryAction={{
          content: "Cancel",
          onAction: () => setShowStoreSelection(false),
          disabled: isSwitchingStore,
        }}
      >
        <Modal.Section>
          <BlockStack gap='300'>
            {isSwitchingStore && (
              <div
                style={{
                  padding: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                }}
              >
                <Spinner size='small' />
                <Text tone='subdued' as='p' variant='bodyMd'>
                  Switching store...
                </Text>
              </div>
            )}
            {storesData?.stores && storesData.stores.length > 0 ? (
              storesData.stores.map((store: any) => (
                <div
                  key={store._id}
                  onClick={() =>
                    !isSwitchingStore && handleStoreSelect(store._id)
                  }
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    padding: "16px",
                    borderRadius: "8px",
                    cursor: isSwitchingStore ? "default" : "pointer",
                    transition: "background-color 0.2s",
                    border: "1px solid transparent",
                    opacity: isSwitchingStore ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSwitchingStore) {
                      e.currentTarget.style.backgroundColor =
                        theme === "dark" ? "#2a2a2a" : "#f6f6f7";
                      e.currentTarget.style.borderColor =
                        theme === "dark" ? "#3a3a3a" : "#e1e3e5";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.borderColor = "transparent";
                  }}
                >
                  <div
                    style={{
                      scale: "1.5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon source={StoreIcon} tone='subdued' />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Text
                        variant='bodyLg'
                        fontWeight='semibold'
                        as='p'
                        tone='base'
                      >
                        {store.name || "Store"}
                      </Text>
                    </div>
                    <div style={{ marginTop: "4px" }}>
                      <Text variant='bodySm' tone='subdued' as='p'>
                        {store.domain ||
                          store.myshopify_domain ||
                          "Select this store"}
                      </Text>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <Text tone='subdued' as='p'>
                No stores available
              </Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Page
        narrowWidth
        title='Bookease'
        secondaryActions={
          <ButtonGroup>
            {!loginMode ? (
              <Button
                variant='secondary'
                onClick={() => setLoginMode(true)}
                disabled={isLoading}
                loading={isLoading}
              >
                Sign in
              </Button>
            ) : (
              <Button
                variant='plain'
                disabled={isLoading}
                loading={isLoading}
                onClick={() => setLoginMode(false)}
              >
                Cancel
              </Button>
            )}
            <label
              className='theme-switch'
              style={{
                display: "inline-flex",
                alignItems: "center",
                ["--toggle-size" as any]: "11.25px",
              }}
            >
              <input
                type='checkbox'
                className='theme-switch__checkbox'
                checked={theme === "dark"}
                onChange={handleThemeChange}
              />
              <div className='theme-switch__container'>
                <div className='theme-switch__clouds'></div>

                <div className='theme-switch__stars-container'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 144 55'
                    fill='none'
                  >
                    <path
                      fillRule='evenodd'
                      clipRule='evenodd'
                      d='M135.831 3.00688C135.055 3.85027 134.111 4.29946 133 4.35447C134.111 4.40947 135.055 4.85867 135.831 5.71123C136.607 6.55462 136.996 7.56303 136.996 8.72727C136.996 7.95722 137.172 7.25134 137.525 6.59129C137.886 5.93124 138.372 5.39954 138.98 5.00535C139.598 4.60199 140.268 4.39114 141 4.35447C139.88 4.2903 138.936 3.85027 138.16 3.00688C137.384 2.16348 136.996 1.16425 136.996 0C136.996 1.16425 136.607 2.16348 135.831 3.00688ZM31 23.3545C32.1114 23.2995 33.0551 22.8503 33.8313 22.0069C34.6075 21.1635 34.9956 20.1642 34.9956 19C34.9956 20.1642 35.3837 21.1635 36.1599 22.0069C36.9361 22.8503 37.8798 23.2903 39 23.3545C38.2679 23.3911 37.5976 23.602 36.9802 24.0053C36.3716 24.3995 35.8864 24.9312 35.5248 25.5913C35.172 26.2513 34.9956 26.9572 34.9956 27.7273C34.9956 26.563 34.6075 25.5546 33.8313 24.7112C33.0551 23.8587 32.1114 23.4095 31 23.3545Z'
                      fill='currentColor'
                    />
                  </svg>
                </div>

                <div className='theme-switch__circle-container'>
                  <div className='theme-switch__sun-moon-container'>
                    <div className='theme-switch__moon'>
                      <div className='theme-switch__spot'></div>
                      <div className='theme-switch__spot'></div>
                      <div className='theme-switch__spot'></div>
                    </div>
                  </div>
                </div>
              </div>
            </label>
          </ButtonGroup>
        }
        primaryAction={
          <Button
            variant={theme === "dark" ? "secondary" : "primary"}
            onClick={() => {
              window.location.href = "https://admin.shopify.com/";
            }}
          >
            Shopify admin
          </Button>
        }
      >
        {loginMode ? (
          <Card>
            <FormLayout>
              <FormLayout.Group>
                <Controller
                  control={control}
                  name='email'
                  rules={{ required: "Email is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      autoComplete='off'
                      label='Email'
                      type='email'
                      requiredIndicator
                      placeholder='Enter email here...'
                      error={errors?.email && "Email is required or invalid"}
                    ></TextField>
                  )}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Controller
                  control={control}
                  name='password'
                  rules={{ required: "Password is required" }}
                  render={({ field }) => (
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "20px",
                          padding: "0",
                          position: "absolute",
                          right: "5px",
                          top: "30px",
                          zIndex: "100000",
                          cursor: "pointer",
                        }}
                        onClick={onShowPasswordHandler}
                      >
                        <Icon source={!showPassword ? ViewIcon : HideIcon} />
                      </span>
                      <TextField
                        {...field}
                        autoComplete='off'
                        label='Password'
                        type={showPassword ? "text" : "password"}
                        placeholder='Enter password here...'
                        requiredIndicator
                        helpText={
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "end",
                              alignItems: "center",
                            }}
                          >
                            <ForgetPassword />
                          </div>
                        }
                        error={errors.password && "Password is required"}
                      ></TextField>{" "}
                    </div>
                  )}
                />
              </FormLayout.Group>

              <div
                style={{
                  // paddingBottom: ".8rem",
                  display: "flex",
                  justifyItems: "start",
                  gap: "5px",
                }}
              >
                <Text as='h1' tone='critical'>
                  {err}
                </Text>
                {err && (
                  <div>
                    <Tooltip content='If you are unable to login please check your email if you did not confirm your email'>
                      <Icon source={InfoIcon} tone='critical' />
                    </Tooltip>
                  </div>
                )}
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <TurnstileWidget
                  key={captchaKey}
                  siteKey='0x4AAAAAACk55mjuovOhaYgd'
                  theme={theme === "dark" ? "dark" : "light"}
                  onVerify={(token) => setTurnstileToken(token)}
                />
              </div>
            </FormLayout>
            <ButtonGroup>
              <Button
                onClick={handleSubmit(userLoginHandler as any)}
                disabled={isLoading || !turnstileToken}
                loading={isLoading}
                variant={theme === "dark" ? "secondary" : "primary"}
              >
                Login
              </Button>
              <Button
                onClick={() => setLoginMode(false)}
                disabled={isLoading}
                loading={isLoading}
                variant={theme === "dark" ? "secondary" : "plain"}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </Card>
        ) : (
          <Banner title='Wellcome to Bookease...'>
            <p>
              We are unable to authorize you. <br /> To authorize yourself,
              <br /> go to
              <strong>
                {" "}
                <Link
                  url='https://apps.shopify.com'
                  dataPrimaryLink
                  monochrome
                  target='_blank'
                >
                  shopify app store
                </Link>
              </strong>{" "}
              and install
              <strong> Bookease</strong>. <br /> If you have, <br /> go to your
              <strong>
                {" "}
                <Link
                  url='https://admin.shopify.com'
                  dataPrimaryLink
                  monochrome
                  target='_blank'
                >
                  shopify store admin
                </Link>{" "}
              </strong>{" "}
              <strong>&gt;</strong>
              <strong> apps</strong> and select
              <strong> Bookease</strong>. <br /> <span>OR</span> <br />
              <span>
                You can directly sign in by clicking on{" "}
                <Button
                  dataPrimaryLink
                  variant={theme === "dark" ? "secondary" : "plain"}
                  onClick={() => {
                    setLoginMode(true);
                  }}
                >
                  Sign in
                </Button>
              </span>
            </p>
          </Banner>
        )}
      </Page>
    </>
  );
};

export default Login;
